import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { LocalizationPipe } from '@abp/ng.core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CxDatepickerComponent } from 'src/app/shared/components/cx-datepicker/cx-datepicker.component';
import { DateRange } from 'src/app/shared/components/cx-datepicker/date-range.model';
import { ImportScope, ImportScopeType, ImportSource } from '../../models';
import { MetricIds } from 'src/core/models/enum/metric-ids.enum';
import { AgentType } from 'src/core/models/enum/agent-type.enum';
import { MetricDefinitionDto } from 'src/core/models/dashboard/metric-definition.dto';
import { MetricDefinitionService } from 'src/core/services/metric-definition/metric-definition.service';

interface MetricOption {
  id: MetricIds;
  labelKey: string;
  source: ImportSource[];
}

@Component({
  selector: 'app-import-scope',
  standalone: true,
  imports: [CommonModule, FormsModule, LocalizationPipe, CxDatepickerComponent],
  templateUrl: './import-scope.component.html',
  styleUrls: ['./import-scope.component.scss'],
})
export class ImportScopeComponent implements OnInit, OnDestroy {
  @Input() systemStartDate: Date | null = null;
  @Input() source: ImportSource = ImportSource.CA;
  @Input() initialScopeType: ImportScopeType = 'full';
  @Input() radioGroupName = '';
  @Output() scopeChange = new EventEmitter<ImportScope>();

  scopeType: ImportScopeType = 'full';
  dateRange: DateRange | null = null;
  selectedMetrics: MetricIds[] = [];
  private metricDefinitions: MetricDefinitionDto[] = [];
  private readonly destroy$ = new Subject<void>();

  private allMetricOptions: MetricOption[] = [
    { id: MetricIds.Aht, labelKey: 'Settings::DataImport:Metrics:Aht', source: [ImportSource.CA, ImportSource.VA] },
    { id: MetricIds.Csat, labelKey: 'Settings::DataImport:Metrics:Csat', source: [ImportSource.CA, ImportSource.VA] },
    { id: MetricIds.CsatPercentage, labelKey: 'Settings::DataImport:Metrics:CsatPercentage', source: [ImportSource.CA, ImportSource.VA] },
    { id: MetricIds.EstimatedCsat, labelKey: 'Settings::DataImport:Metrics:EstimatedCsat', source: [ImportSource.CA, ImportSource.VA] },
    { id: MetricIds.EstimatedCsatPercentage, labelKey: 'Settings::DataImport:Metrics:EstimatedCsatPercentage', source: [ImportSource.CA, ImportSource.VA] },
    { id: MetricIds.Nps, labelKey: 'Settings::DataImport:Metrics:Nps', source: [ImportSource.CA, ImportSource.VA] },
    { id: MetricIds.NegativeSentiment, labelKey: 'Settings::DataImport:Metrics:NegativeSentiment', source: [ImportSource.CA, ImportSource.VA] },
    { id: MetricIds.Nonfcr, labelKey: 'Settings::DataImport:Metrics:Nonfcr', source: [ImportSource.CA, ImportSource.VA] },
    { id: MetricIds.TransferRate, labelKey: 'Settings::DataImport:Metrics:TransferRate', source: [ImportSource.CA] },
    { id: MetricIds.ResolutionType, labelKey: 'Settings::DataImport:Metrics:ResolutionType', source: [ImportSource.VA] },
    
  ];

  get metricOptions(): MetricOption[] {
    const requiredAgentType = this.source === ImportSource.CA ? AgentType.Human : AgentType.AI;
    return this.allMetricOptions.filter(metric => {
      if (!metric.source.includes(this.source)) return false;
      if (this.metricDefinitions.length === 0) return true;
      const def = this.metricDefinitions.find(d => d.metricId === metric.id);
      if (!def || def.supportedAgentTypes.length === 0) return true;
      return (
        def.supportedAgentTypes.includes(requiredAgentType) ||
        def.supportedAgentTypes.includes(AgentType.Both)
      );
    });
  }

  get scopeTypeGroupName(): string {
    return this.radioGroupName || `scopeType-${this.source}`;
  }

  constructor(private metricDefinitionService: MetricDefinitionService) {}

  ngOnInit(): void {
    this.scopeType = this.initialScopeType;
    this.emitScope();
    this.metricDefinitionService
      .getDefinitions()
      .pipe(takeUntil(this.destroy$))
      .subscribe(defs => {
        this.metricDefinitions = defs;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onScopeTypeChange(): void {
    if (this.scopeType === 'full') {
      this.dateRange = null;
      this.selectedMetrics = [];
    }
    this.emitScope();
  }

  onDateRangeChange(range: DateRange): void {
    this.dateRange = range;
    this.emitScope();
  }

  onMetricToggle(metricId: MetricIds): void {
    const index = this.selectedMetrics.indexOf(metricId);
    if (index > -1) {
      this.selectedMetrics.splice(index, 1);
    } else {
      this.selectedMetrics.push(metricId);
    }
    this.emitScope();
  }

  isMetricSelected(metricId: MetricIds): boolean {
    return this.selectedMetrics.includes(metricId);
  }

  get isAllMetricsSelected(): boolean {
    return this.metricOptions.length > 0 && this.metricOptions.every(m => this.isMetricSelected(m.id));
  }

  onToggleAllMetrics(): void {
    if (this.isAllMetricsSelected) {
      this.selectedMetrics = [];
    } else {
      this.selectedMetrics = this.metricOptions.map(m => m.id);
    }
    this.emitScope();
  }

  private emitScope(): void {
    const scope: ImportScope = {
      type: this.scopeType,
      ...(this.scopeType === 'customized' && {
        dateRange: this.dateRange ? { start: this.dateRange.start, end: this.dateRange.end } : undefined,
        selectedMetrics: this.selectedMetrics.map(m => m.toString()),
      }),
    };
    this.scopeChange.emit(scope);
  }
}

